import { resolveLinkUrl } from '~~/shared-components/composables/redirects'
import { linkTypesQuery } from '~~/shared-components/queries/link'
import type { LinkTypeObject } from '~~/shared-components/types/Link'
import type { DateObject } from '~~/shared-components/types/Objects'

type PreviewParams = {
  idOrKey?: number | string
  action?: string
  ttl?: number
  redirect?: boolean
  target?: '_blank' | '_parent' | '_self' | '_top'
  query?: object
  withSignature?: boolean
  type: string
}

export const usePreview = () => {
  const preview = async ({
    idOrKey,
    type,
    action,
    ttl = 60 * 2,
    withSignature = false,
    // Při vypnutém SSR v ERP dochází ke konfliktu proto alias
    redirect: asRedirect = true,
    target = '_blank',
    query,
  }: PreviewParams) => {
    const { get } = useApi()
    const { dataLang } = useLocale()

    const getSignature = async () => {
      const isArticleNameKey = Number.isNaN(parseInt(idOrKey as string))

      const id = isArticleNameKey ? undefined : idOrKey
      const key = isArticleNameKey ? idOrKey : undefined

      const res = await get<{ url: string; expiration: DateObject }>('/previews', {
        type,
        ...(id
          ? { id }
          : {
              byKey: 1,
              key,
            }),
        ttl,
        absolute: 1,
      })
      const url = new URL(res.data.url)
      const urlParams = new URLSearchParams(url.search)

      const signature = urlParams.get('signature')
      const expires = urlParams.get('expires')
      const origin = `${url.origin}/api`

      return { signature, expires, origin }
    }

    const signature = withSignature ? await getSignature() : {}

    const nuxtApp = useNuxtApp()

    return await nuxtApp.runWithContext(async () => {
      const queryClient = useQueryClient()
      const linkTypes = await queryClient.fetchQuery(linkTypesQuery())
      const resolvedAction = action ?? linkTypes[type as LinkTypeObject['value']]?.action

      if (!resolvedAction) {
        throw new Error(`No preview action resolved for type "${type}"`)
      }

      const url = await resolveLinkUrl({
        action: resolvedAction,
        params: {
          ...(idOrKey ? { id: `${idOrKey}` } : {}),
          ...signature,
          ...query,
          locale: dataLang.value,
        },
      })

      return asRedirect ? window.open(url, target) : url
    })
  }

  return { preview }
}
